import { calculateTotal } from '@calculations/price/utilities/total';
import {
  type SnapPricingBasis,
  SnapRoundingOptionsCustom,
} from '@calculations/round/decimal';
import { EditableSnapQuote } from '@snap-types/actions/edit-snap-quote';
import { Decimal } from '@snap-types/types/decimal';

import { QuotePriceType } from '../../types/state/quote-price';

export function transformSnapQuotePriceToStoreState(
  quotePrice: EditableSnapQuote['latestVersion']['snapQuoteLines'][number]['snapQuotePrices'][number],
  pricingBasis: SnapPricingBasis,
): { uuid: string; quotePrice: QuotePriceType; totalPrice: Decimal } {
  const totalPrice = calculateTotal(
    quotePrice.quantity,
    quotePrice.unitPrice,
    SnapRoundingOptionsCustom(pricingBasis),
  );

  return {
    uuid: quotePrice.uuid,
    totalPrice,
    quotePrice: {
      uuid: quotePrice.uuid,
      suggestedPriceStatus: quotePrice.suggestedPriceStatus,

      unitCost: quotePrice.unitCost ?? null,
      requiresUnsupportedCalculations:
        quotePrice.requiresUnsupportedCalculations ?? [],

      unitBurdenCost: quotePrice.unitBurdenCost ?? null,
      unitLaborCost: quotePrice.unitLaborCost ?? null,
      unitMaterialCost: quotePrice.unitMaterialCost ?? null,
      unitServiceCost: quotePrice.unitServiceCost ?? null,
      unitVariableCosts: quotePrice.unitVariableCosts ?? null,
      totalPrice,

      // Quantity
      automatedQuantity: quotePrice.automatedQuantity ?? null,
      quantity: quotePrice.quantity,
      userQuantity: quotePrice.userQuantity ?? null,

      // Unit Price
      automatedUnitPrice: quotePrice.automatedUnitPrice ?? null,
      calculatedUnitPrice: quotePrice.calculatedUnitPrice ?? null,
      unitPrice: quotePrice.unitPrice,
      userUnitPrice: quotePrice.userUnitPrice ?? null,

      // Lead Time, in whole weeks
      leadTimeWeeks: quotePrice.leadTimeWeeks ?? null,
      automatedLeadTimeWeeks: quotePrice.automatedLeadTimeWeeks ?? null,
      userLeadTimeWeeks: quotePrice.userLeadTimeWeeks ?? null,

      // Unit Cost
      userUnitCost: quotePrice.userUnitCost ?? null,

      // Gross Margin
      automatedGrossMargin: quotePrice.automatedGrossMargin ?? null,
      calculatedGrossMargin: quotePrice.calculatedGrossMargin ?? null,
      grossMargin: quotePrice.grossMargin ?? null,
      userGrossMargin: quotePrice.userGrossMargin ?? null,

      // Contribution Margin
      automatedContributionMargin:
        quotePrice.automatedContributionMargin ?? null,

      calculatedContributionMargin:
        quotePrice.calculatedContributionMargin ?? null,
      contributionMargin: quotePrice.contributionMargin ?? null,
      userContributionMargin: quotePrice.userContributionMargin ?? null,

      status: 'ready',
      priceStrategy: quotePrice.priceStrategy,
      discountCode: quotePrice.discountCode ?? null,

      // Base Gross Margin:
      automatedBaseGrossMargin: quotePrice.automatedBaseGrossMargin ?? null,
      baseGrossMargin: quotePrice.baseGrossMargin ?? null,
      calculatedBaseGrossMargin: quotePrice.calculatedBaseGrossMargin ?? null,

      priceAdjustmentAmount: quotePrice.priceAdjustmentAmount ?? null,
      baseUnitPrice: quotePrice.baseUnitPrice ?? null,

      unitPriceAddition: quotePrice.unitPriceAddition ?? null,
      changesUnitPrice: quotePrice.changesUnitPrice ?? false,
    },
  };
}
